import { useState } from 'react';
import { distinct } from '@/utils/arrayUtils';
import { MerchantContainerInformationNumberOnly } from '../../../views/MerchantComponents/MerchantModel';
import i18n from '@/utils/i18n';
import { centsToPrice } from '@/utils/formatter';
import { IconButton } from '../../../components/Button/Button';
import { FaCheck } from 'react-icons/fa';
import { EditItem, EditSection } from '../../../components/Layout';

export enum PriceTypes {
  TRANSACTION = 'TRANSACTION',
}

type PriceDifferenceProps = {
  containerPrices: MerchantContainerInformationNumberOnly[];
  onResolve: (type: PriceTypes, value: number, containerTypeId: number) => void;
  currency: string;
};

export const PriceDifference = ({
  containerPrices,
  onResolve,
  currency,
}: PriceDifferenceProps) => {
  // Group containerPrices by containerTypeId
  const groupedContainerPrices: Record<
    number,
    MerchantContainerInformationNumberOnly[]
  > = {};
  containerPrices.forEach((cp) => {
    if (!groupedContainerPrices[cp.containerTypeId]) {
      groupedContainerPrices[cp.containerTypeId] = [];
    }
    groupedContainerPrices[cp.containerTypeId].push(cp);
  });

  // Calculate differences for each group
  const differences = Object.entries(groupedContainerPrices).map(
    ([containerTypeId, prices]) => {
      // Get unique values for each attribute
      const uniqueTransactionPrices = prices
        .map((cp) => cp.transactionPrice)
        .filter((p, i, arr) => distinct(p, i, arr));
      const uniqueStoreLostCosts = prices
        .map((cp) => cp.storeLostCost)
        .filter((p, i, arr) => distinct(p, i, arr));
      const uniqueStoreLostCostUppers = prices
        .map((cp) => cp.storeLostCostUpper)
        .filter((p, i, arr) => distinct(p, i, arr));
      const uniqueStoreLostCostLowers = prices
        .map((cp) => cp.storeLostCostLower)
        .filter((p, i, arr) => distinct(p, i, arr));

      return {
        containerTypeId: parseInt(containerTypeId),
        uniqueTransactionPrices,
        uniqueStoreLostCosts,
        uniqueStoreLostCostUppers,
        uniqueStoreLostCostLowers,
      };
    },
  );

  // State for selected options
  const [selectedOptions, setSelectedOptions] = useState<any>({});

  // Handle change of selected option
  const handleOptionChange = (
    containerTypeId: number,
    attribute: string,
    value: string,
  ) => {
    setSelectedOptions({
      ...selectedOptions,
      [containerTypeId]: {
        ...selectedOptions[containerTypeId],
        [attribute]: value,
      },
    });
  };

  // Display differences and options to resolve conflicts
  return (
    <EditSection
      title={i18n.t('Common:priceDifferences')}
      className="mt-3 mb-3"
    >
      <EditItem title={i18n.t('Common:transactionPriceDifferenceLabel')}>
        <table className="table-striped table-borderless table-hover table bg-white">
          <thead>
            <tr>
              <th>Container Type ID</th>
              <th>Different Prices</th>
              <th>Enter Price</th>
              <th>Resolve</th>
            </tr>
          </thead>
          <tbody>
            {[...differences]
              .filter(
                (difference) => difference.uniqueTransactionPrices.length > 1,
              )
              .sort((a, b) => a.containerTypeId - b.containerTypeId)
              .map((difference) => (
                <tr key={difference.containerTypeId}>
                  <td>{difference.containerTypeId}</td>
                  <td>{difference.uniqueTransactionPrices.join('; ')}</td>
                  <td>
                    <div className="input-group mb-3">
                      <input
                        value={
                          selectedOptions[difference.containerTypeId]
                            ?.transactionPrice || ''
                        }
                        type="text"
                        className="form-control"
                        onChange={(e) =>
                          handleOptionChange(
                            difference.containerTypeId,
                            'transactionPrice',
                            e.target.value.replace(/\D+/, ''),
                          )
                        }
                      />
                      <span className="input-group-text">
                        {' '}
                        {centsToPrice(
                          parseInt(
                            selectedOptions[difference.containerTypeId]
                              ?.transactionPrice || '',
                          ),
                          currency,
                        )}
                      </span>
                    </div>
                  </td>
                  <td>
                    <IconButton
                      className="me-2 mb-2"
                      icon={<FaCheck />}
                      label="Resolve"
                      onClick={() => {
                        if (
                          // eslint-disable-next-line no-restricted-globals
                          confirm(
                            'Set all container transaction prices for the invoice collection stores to ' +
                              centsToPrice(
                                parseInt(
                                  selectedOptions[difference.containerTypeId]
                                    ?.transactionPrice,
                                ),
                                currency,
                              ),
                          )
                        ) {
                          onResolve(
                            PriceTypes.TRANSACTION,
                            parseInt(
                              selectedOptions[difference.containerTypeId]
                                .transactionPrice,
                            ) / 100,
                            difference.containerTypeId,
                          );
                        }
                      }}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </EditItem>
    </EditSection>
  );
};
